"use client";

import { Activity, ArrowRight, ShieldCheck, ShieldAlert, ShieldQuestionMark as ShieldQuestion, ShieldCheck as ShieldShield } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurDefenseSuite() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    icon: ShieldCheck,
    title: "AI Threat Detection",
    description: "Leverage advanced machine learning to identify and neutralize cyber threats in real-time before they impact your critical business operations.",
    items: "Pattern Analysis · Anomaly Detection · Real-time Alerts",
    link: "/AI Threat Detection"
  }, {
    icon: ShieldAlert,
    title: "24/7 Security Monitoring",
    description: "Our dedicated security operations center provides continuous surveillance, ensuring your digital perimeter remains impenetrable around the clock.",
    items: "Continuous Logging · Incident Triage · Threat Hunting",
    link: "/24/7 Security Monitoring"
  }, {
    icon: ShieldQuestion,
    title: "Phishing Prevention",
    description: "Protect your workforce from sophisticated social engineering attacks with our intelligent email filtering and proactive verification systems.",
    items: "Email Sanitization · Link Analysis · User Awareness",
    link: "/Phishing Prevention"
  }, {
    icon: Activity,
    title: "Vulnerability Assessments",
    description: "Systematically identify, classify, and prioritize vulnerabilities in your infrastructure to ensure your defenses are always one step ahead.",
    items: "Network Scanning · Risk Prioritization · Remediation Roadmap",
    link: "/Vulnerability Assessments"
  }];
  return <section data-section-id="984" id="our-defense-suite" className="relative overflow-hidden bg-background py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.04]">
        <ShieldShield className="absolute top-20 left-[8%] size-32 text-foreground -rotate-12" strokeWidth={1} />
        <Activity className="absolute top-1/3 right-[6%] size-40 text-foreground rotate-12" strokeWidth={1} />
        <ShieldCheck className="absolute bottom-32 left-[15%] size-28 text-foreground rotate-6" strokeWidth={1} />
        <ShieldAlert className="absolute bottom-20 right-[18%] size-36 text-foreground -rotate-6" strokeWidth={1} />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="mb-6 text-xs font-medium tracking-[0.2em] uppercase text-primary">
            § 01 / Expertise
          </p>
          <h2 className="font-default text-4xl md:text-6xl leading-[1.0] tracking-tighter text-foreground">
            AI-Powered Cybersecurity
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-xl leading-relaxed text-muted-foreground">
            InfinitySecure delivers elite, AI-driven protection designed to safeguard your digital assets against the most sophisticated modern threats.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {services.map((service, i) => {
          const Icon = service.icon;
          return <Card data-index={i} key={i} className="group relative bg-card p-10 rounded-lg border border-border transition-all duration-500 ease-out hover:border-primary">
                {}
                <div className="mb-8 flex size-[88px] items-center justify-center rounded-lg bg-accent transition-colors duration-500 group-hover:bg-primary/10">
                  <Icon className="size-9 text-primary transition-transform duration-500 group-hover:scale-110" strokeWidth={1.5} />
                </div>

                {}
                <h3 className="font-default text-2xl leading-tight text-foreground mb-4">
                  {service.title}
                </h3>

                {}
                <p className="text-base leading-relaxed text-muted-foreground mb-6">
                  {service.description}
                </p>

                {}
                <Separator className="bg-border my-6" />

                {}
                <p className="text-sm text-muted-foreground mb-8 tracking-wide font-mono">
                  {service.items}
                </p>

                {}
                <Link to={service.link} className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link">
                  <span>Learn more</span>
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                </Link>
              </Card>;
        })}
        </div>

        {}
        <div className="mt-16 flex justify-center">
          <Button asChild variant="link" className="text-primary hover:text-primary/80 text-base font-medium group">
            <Link to="/ExpertisePage" className="inline-flex items-center gap-2">
              <span>Explore all services</span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}